import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Bot, Link2, Copy, Download, ExternalLink, Unplug, Users, Smartphone } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import { Card, CardHeader, CardBody, Button, Field, Input, ConfirmDialog, LoadingBlock } from '../../ui';

/** Telegram-бот компанії для співробітників без застосунку (Android): інструкція, підключення, QR */
export default function TelegramBotTab({ tenant }) {
  const { t } = useI18n();
  const { canEdit } = useSession();
  const [info, setInfo] = useState(null);
  const [confirmOff, setConfirmOff] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/tenants/${tenant.id}/telegram-bot`)
      .then((res) => setInfo(res.data.data))
      .catch((err) => { setInfo({ configured: false }); toast.error(errorText(err, t('common.error'))); });
  }, [tenant.id, t]);
  useEffect(() => { load(); }, [load]);

  if (!info) return <LoadingBlock />;

  const disconnect = async () => {
    setBusy(true);
    try {
      const res = await api.delete(`/admin/tenants/${tenant.id}/telegram-bot`);
      setInfo(res.data.data);
      setConfirmOff(false);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const invite = t('tgBot.inviteText', { company: tenant.name, link: info.link || '' });
  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(invite);
      toast.success(t('tgBot.copied'));
    } catch {
      toast.error(t('common.error'));
    }
  };

  return (
    <div className="space-y-6">
      {info.configured ? (
        <div className="grid gap-6 xl:grid-cols-3">
          <Card className="xl:col-span-2">
            <CardHeader title={t('tgBot.connectedTitle')} />
            <CardBody className="space-y-4">
              <div className="flex flex-wrap items-center gap-3">
                <Bot className="h-5 w-5 text-brand-600" />
                <a href={info.link} target="_blank" rel="noreferrer" className="text-base font-semibold text-brand-600 hover:underline">@{info.username}</a>
                <span className="flex items-center gap-1.5 text-sm text-zinc-500"><Users className="h-4 w-4" />{t('tgBot.linked', { count: info.linked_employees })}</span>
              </div>
              <p className="text-sm text-zinc-600 dark:text-zinc-400">{t('tgBot.howEmployees')}</p>
              <div className="rounded-xl bg-zinc-50 p-3 text-sm whitespace-pre-line text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300">{invite}</div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" icon={Copy} onClick={copyInvite}>{t('tgBot.copyInvite')}</Button>
                <a href={info.link} target="_blank" rel="noreferrer"><Button variant="secondary" icon={ExternalLink}>{t('tgBot.open')}</Button></a>
              </div>
              {canEdit && (
                <div className="space-y-3 border-t border-zinc-100 pt-4 dark:border-zinc-800">
                  <p className="text-sm text-zinc-500">{t('tgBot.replace')}</p>
                  <TokenForm tenantId={tenant.id} onSaved={setInfo} />
                  <Button variant="danger-ghost" icon={Unplug} onClick={() => setConfirmOff(true)}>{t('tgBot.disconnect')}</Button>
                </div>
              )}
            </CardBody>
          </Card>
          <Card>
            <CardHeader title={t('tgBot.qrTitle')} />
            <CardBody className="flex flex-col items-center gap-3 text-center">
              {info.qr && <img src={info.qr} alt={`QR @${info.username}`} className="h-48 w-48 rounded-xl bg-white p-2" />}
              <p className="text-xs text-zinc-500">{t('tgBot.qrHint')}</p>
              {info.qr && <a href={info.qr} download={`telegram-${info.username}.png`}><Button variant="secondary" size="sm" icon={Download}>{t('tgBot.downloadQr')}</Button></a>}
            </CardBody>
          </Card>
        </div>
      ) : (
        <Card>
          <CardHeader title={t('tgBot.title')} />
          <CardBody className="space-y-5">
            <div className="flex items-start gap-3">
              <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
              <p className="max-w-2xl text-sm text-zinc-600 dark:text-zinc-400">{t('tgBot.intro')}</p>
            </div>
            <Steps />
            {canEdit ? <TokenForm tenantId={tenant.id} onSaved={setInfo} /> : <p className="text-sm text-zinc-500">{t('tgBot.viewOnly')}</p>}
          </CardBody>
        </Card>
      )}

      {info.configured && (
        <Card>
          <CardHeader title={t('tgBot.stepsTitle')} />
          <CardBody><Steps compact /></CardBody>
        </Card>
      )}

      <ConfirmDialog
        open={confirmOff}
        onClose={() => setConfirmOff(false)}
        onConfirm={disconnect}
        loading={busy}
        tone="danger"
        title={t('tgBot.disconnect')}
        text={t('tgBot.disconnectConfirm')}
        confirmLabel={t('tgBot.disconnect')}
      />
    </div>
  );
}

/** Покрокова інструкція: як створити бота в @BotFather */
function Steps({ compact }) {
  const { t } = useI18n();
  const steps = [1, 2, 3, 4, 5, 6];
  return (
    <ol className="max-w-3xl space-y-3">
      {steps.map((n) => (
        <li key={n} className="flex gap-3">
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700 dark:bg-brand-950/60 dark:text-brand-300">{n}</span>
          <div className="min-w-0 text-sm">
            <p className="font-medium text-zinc-900 dark:text-zinc-100">{t(`tgBot.steps.s${n}.title`)}</p>
            {!compact && <p className="mt-0.5 text-zinc-500">{t(`tgBot.steps.s${n}.text`)}</p>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function TokenForm({ tenantId, onSaved }) {
  const { t } = useI18n();
  const [token, setToken] = useState('');
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.put(`/admin/tenants/${tenantId}/telegram-bot`, { token: token.trim() });
      toast.success(t('tgBot.connected', { name: res.data.data.username }));
      setToken('');
      onSaved(res.data.data);
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="flex max-w-2xl flex-col gap-2 sm:flex-row sm:items-end">
      <Field label={t('tgBot.token')} className="flex-1">
        <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="1234567890:AA…" autoComplete="off" spellCheck={false} />
      </Field>
      <Button type="submit" icon={Link2} loading={busy} disabled={token.trim().length < 20}>{t('tgBot.connect')}</Button>
    </form>
  );
}
